<template>
  <div
    class="pointer-events-none fixed inset-x-0 top-6 z-[100] flex flex-col items-center gap-3 px-4 sm:top-8 sm:px-6"
  >
    <TransitionGroup
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="translate-y-[-1rem] opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div
        v-for="(toast, index) in items"
        :key="toast.id ?? index"
        class="pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-2xl p-4 shadow-2xl ring-1 ring-slate-900/5 dark:ring-white/10 bg-white dark:!bg-slate-800 dark:bg-slate-900"
      >
        <component
          :is="iconFor(toast.type)"
          class="h-6 w-6 shrink-0"
          :class="iconColorClass(toast.type)"
        />
        <div class="flex-1 min-w-0 break-words">
          <p class="text-sm font-bold text-slate-900 dark:!text-white dark:text-slate-50">
            {{ toast.message }}
          </p>
          <p
            v-if="toast.detail"
            class="mt-0.5 text-sm text-slate-500 dark:!text-slate-300 dark:text-slate-400"
          >
            {{ toast.detail }}
          </p>
        </div>

        <div v-if="toast.action" class="shrink-0 ml-2">
          <a
            :href="toast.action.url"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center justify-center rounded-lg px-4 py-2 text-xs font-bold text-white shadow-sm transition active:scale-95 bg-primary-600 hover:bg-primary-500 dark:!bg-primary-500 dark:hover:!bg-primary-400"
          >
            {{ toast.action.label }}
          </a>
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<script setup lang="ts">
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  InformationCircleIcon,
} from '@heroicons/vue/24/solid';

interface ToastAction {
  label: string;
  url: string;
}

interface ToastItem {
  id?: string | number;
  type: 'success' | 'info' | 'warning' | 'error';
  message: string;
  detail?: string;
  action?: ToastAction;
}

defineProps<{
  items: ToastItem[];
}>();

function iconFor(type: ToastItem['type']) {
  switch (type) {
    case 'success':
      return CheckCircleIcon;
    case 'warning':
      return ExclamationTriangleIcon;
    case 'error':
      return XCircleIcon;
    default:
      return InformationCircleIcon;
  }
}

function iconColorClass(type: ToastItem['type']) {
  switch (type) {
    case 'success':
      return 'text-emerald-500 dark:!text-emerald-400';
    case 'warning':
      return 'text-amber-500 dark:!text-amber-400';
    case 'error':
      return 'text-rose-500 dark:!text-rose-400';
    default:
      return 'text-primary-500 dark:!text-primary-400';
  }
}
</script>
